import { formatPrice } from "../../../../common/utils"
import { mapPropertyUnbookable } from "../../../../common/map/marker"

/* Toolkit map-stacking port (#854) — the pure halves: co-location grouping,
   stacked-badge price ranges, cluster tier math, and spiderfy fan geometry.
   Everything here is provider-free so it unit-tests without booting Leaflet;
   the Leaflet wiring lives in LeafletMap.tsx + spiderfyController.ts. */

export type StackableProperty = {
  id: string
  price: number
  currency?: string | null
  /**
   * Calendar-derived freshness trio, as MapProperty carries them (#1188
   * contract, common/map/types.ts): `lowestPrice: null` on a calendar Central
   * vouches for = unbookable, and its base rate must not enter a stacked
   * price range (#1310). Absent keys are not a signal and keep the price.
   */
  lowestPrice?: number | null
  bookableNights?: number | null
  calendarStale?: boolean
  location?: { lat?: number; lng?: number }
}

export type StackingConfig = {
  enabled?: boolean
  clusterMode?: boolean
  fadeOpacityPct?: number
  badgeBg?: string
  badgeText?: string
  countBg?: string
  countText?: string
  centerDot?: string
  connector?: string
  focus?: string
  clusterColor1?: string
  clusterColor2?: string
  clusterColor3?: string
  clusterColor4?: string
  clusterColor5?: string
}

/* Toolkit Admin\MapStacking\Page::DEFAULTS. Per the #852 absence rule these
   live here at the consumer, never as schema defaults — an absent leaf must
   stay absent in stored settings so widget saves can't clobber feed-globals. */
export const STACKING_DEFAULTS = {
  fadeOpacityPct: 40,
  badgeBg: '#1a1a2e',
  badgeText: '#ffffff',
  countBg: '#058ef9',
  countText: '#ffffff',
  centerDot: '#1a1a2e',
  connector: '#1a1a2e',
  focus: '#3b82f6',
  clusterColor1: '#058ef9',
  clusterColor2: '#0b7fd4',
  clusterColor3: '#7a5cd6',
  clusterColor4: '#d1568a',
  clusterColor5: '#e2603f',
} as const

export type ResolvedStacking = {
  enabled: boolean
  clusterMode: boolean
  fadeOpacity: number
} & { [K in keyof Omit<typeof STACKING_DEFAULTS, 'fadeOpacityPct'>]: string }

export function resolveStacking(config: StackingConfig | undefined): ResolvedStacking {
  const enabled = config?.enabled === true
  return {
    enabled,
    // Cluster-primary is the toolkit's (and WP core's) default: absent = on.
    clusterMode: enabled && config?.clusterMode !== false,
    fadeOpacity: (config?.fadeOpacityPct ?? STACKING_DEFAULTS.fadeOpacityPct) / 100,
    badgeBg: config?.badgeBg || STACKING_DEFAULTS.badgeBg,
    badgeText: config?.badgeText || STACKING_DEFAULTS.badgeText,
    countBg: config?.countBg || STACKING_DEFAULTS.countBg,
    countText: config?.countText || STACKING_DEFAULTS.countText,
    centerDot: config?.centerDot || STACKING_DEFAULTS.centerDot,
    connector: config?.connector || STACKING_DEFAULTS.connector,
    focus: config?.focus || STACKING_DEFAULTS.focus,
    clusterColor1: config?.clusterColor1 || STACKING_DEFAULTS.clusterColor1,
    clusterColor2: config?.clusterColor2 || STACKING_DEFAULTS.clusterColor2,
    clusterColor3: config?.clusterColor3 || STACKING_DEFAULTS.clusterColor3,
    clusterColor4: config?.clusterColor4 || STACKING_DEFAULTS.clusterColor4,
    clusterColor5: config?.clusterColor5 || STACKING_DEFAULTS.clusterColor5,
  }
}

/* CSS custom properties consumed by the stacking rules in explorer.css.
   Inline on the map container (the controlBg precedent), so they ride into
   the shadow root without a global stylesheet write. */
export function stackingCssVars(s: ResolvedStacking): Record<string, string> {
  return {
    '--hfse-stack-badge-bg': s.badgeBg,
    '--hfse-stack-badge-text': s.badgeText,
    '--hfse-stack-count-bg': s.countBg,
    '--hfse-stack-count-text': s.countText,
    '--hfse-stack-center-dot': s.centerDot,
    '--hfse-stack-connector': s.connector,
    '--hfse-stack-focus': s.focus,
    '--hfse-cluster-1': s.clusterColor1,
    '--hfse-cluster-2': s.clusterColor2,
    '--hfse-cluster-3': s.clusterColor3,
    '--hfse-cluster-4': s.clusterColor4,
    '--hfse-cluster-5': s.clusterColor5,
    '--hfse-cluster-mask': `url("${CLUSTER_MARKER_DATA_URI}")`,
  }
}

/* Group properties sharing an EXACT coordinate pair (multi-unit buildings).
   String key, no rounding — same semantics as the toolkit, which stacks only
   listings the PMS placed on the identical point. Returns ONLY groups of 2+. */
export function buildStackGroups(props: StackableProperty[]): Map<string, string[]> {
  const all = new Map<string, string[]>()
  for (const p of props) {
    const lat = p.location?.lat
    const lng = p.location?.lng
    if (!lat || !lng) continue
    const key = `${lat},${lng}`
    const ids = all.get(key)
    if (ids) ids.push(p.id)
    else all.set(key, [p.id])
  }
  for (const [key, ids] of all) {
    if (ids.length < 2) all.delete(key)
  }
  return all
}

export function parseStackKey(key: string): { lat: number; lng: number } {
  const comma = key.indexOf(',')
  return { lat: parseFloat(key.slice(0, comma)), lng: parseFloat(key.slice(comma + 1)) }
}

/* "$100 – $200" across the group's card prices; equal (or a single priced
   member) collapses to one figure; no priced members → '' (count-only badge,
   the toolkit's price-display-none path). #1310: an unbookable member
   (mapPropertyUnbookable — calendar-derived lowestPrice: null on a fresh
   calendar, #1307) is skipped too: the pill must never advertise a base rate
   nobody can book; every member unbookable → the count-only badge. */
export function getPriceRangeText(props: StackableProperty[]): string {
  let low = Infinity
  let high = -Infinity
  let currency: string | null | undefined
  for (const p of props) {
    if (!(p.price > 0)) continue
    if (mapPropertyUnbookable(p)) continue
    if (!currency) currency = p.currency
    if (p.price < low) low = p.price
    if (p.price > high) high = p.price
  }
  if (low === Infinity) return ''
  const lowText = formatPrice(low, currency || 'USD')
  if (low === high) return lowText
  return `${lowText} – ${formatPrice(high, currency || 'USD')}`
}

/* Cluster size tiers — mirror the toolkit (which mirrors the Google
   clusterer the WP explorer ships): <10, <25, <50, <100, 100+. */
export const CLUSTER_SIZES = [53, 56, 66, 78, 90] as const

export function clusterTier(count: number): number {
  if (count < 10) return 0
  if (count < 25) return 1
  if (count < 50) return 2
  if (count < 100) return 3
  return 4
}

/* Spiderfy fan geometry in container-pixel space. ≤8 → even circle starting
   at 12 o'clock; >8 → golden-angle spiral whose radius grows 10px per leaf.
   Same constants as the toolkit. */
export const SPIDER_RADIUS_PX = 70
export const MAX_CIRCLE_COUNT = 8

export type Point = { x: number; y: number }

export function getSpiderOffsets(count: number): Point[] {
  if (count > MAX_CIRCLE_COUNT) {
    return Array.from({ length: count }, (_, i) => {
      const angle = i * 0.618 * 2 * Math.PI
      const radius = SPIDER_RADIUS_PX + i * 10
      return { x: radius * Math.cos(angle), y: radius * Math.sin(angle) }
    })
  }
  return Array.from({ length: count }, (_, i) => {
    const angle = (2 * Math.PI * i) / count - Math.PI / 2
    return { x: SPIDER_RADIUS_PX * Math.cos(angle), y: SPIDER_RADIUS_PX * Math.sin(angle) }
  })
}

/* The bundled cluster marker (toolkit assets/icons/cluster.svg): a solid core
   plus concentric halo arcs at 0.5/0.3/0.1 opacity. Painted as a CSS MASK —
   only the alpha is read; the visible colour is --hfse-cluster-N — so one hex
   per tier tints core and halos together. Inlined as a data URI so the mask is
   never a second request (a blocked/404'd mask hides every cluster outright). */
export const CLUSTER_MARKER_DATA_URI =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32"><g fill="black">' +
    '<circle cx="16" cy="16" r="7"/>' +
    '<path opacity=".1" d="M16,2c4,0,7.5,1.7,10.1,4.3l1.4-1.5C24.5,1.9,20.5,0,16,0C11.6,0,7.6,1.8,4.7,4.7l1.4,1.4C8.6,3.6,12.1,2,16,2z"/>' +
    '<path opacity=".3" d="M25.4,7C23,4.6,19.7,3,16,3c-3.6,0-6.8,1.5-9.2,3.8l1.4,1.4C10.2,6.2,13,5,16,5c3.2,0,6,1.3,8,3.5L25.4,7z"/>' +
    '<path opacity=".5" d="M8.9,8.9l1.4,1.4C11.8,8.9,13.8,8,16,8c2.4,0,4.5,1,6,2.7l1.4-1.5C21.5,7.2,18.9,6,16,6C13.2,6,10.7,7.1,8.9,8.9z"/>' +
    '<path opacity=".1" d="M31.6,12.4L29.7,13c0.2,1,0.3,2,0.3,3c0,6.4-4.4,11.9-10.3,13.5l0.5,1.9C27,29.6,32,23.4,32,16C32,14.8,31.8,13.6,31.6,12.4z"/>' +
    '<path opacity=".1" d="M2,16c0-1.1,0.2-2.2,0.4-3.3l-1.9-0.6C0.2,13.4,0,14.7,0,16c0,7.4,5,13.6,11.8,15.4l0.5-1.9C6.4,27.9,2,22.4,2,16z"/>' +
    '<path opacity=".3" d="M29,16c0-0.9-0.1-1.9-0.3-2.7l-1.9,0.6c0.1,0.7,0.2,1.4,0.2,2.2c0,5-3.4,9.3-8,10.6l0.5,1.9C24.9,27,29,22,29,16z"/>' +
    '<path opacity=".3" d="M5,16c0-0.8,0.1-1.7,0.3-2.4L3.4,13C3.1,14,3,15,3,16c0,6,4.1,11,9.6,12.5l0.5-1.9C8.4,25.3,5,21,5,16z"/>' +
    '<path opacity=".5" d="M26,16c0-0.6-0.1-1.3-0.2-1.9l-1.9,0.6C24,15.1,24,15.6,24,16c0,3.6-2.4,6.7-5.7,7.7l0.5,1.9C22.9,24.4,26,20.6,26,16z"/>' +
    '<path opacity=".5" d="M8,16c0-0.5,0.1-1.1,0.2-1.6l-1.9-0.6C6.1,14.5,6,15.3,6,16c0,4.6,3.1,8.4,7.3,9.6l0.5-1.9C10.4,22.7,8,19.6,8,16z"/>' +
    '</g></svg>'
  )

/* Cluster bubble divIcon HTML — the tier class routes the CSS mask + tint. */
export function clusterIconHtml(count: number): { html: string; size: number } {
  const tier = clusterTier(count)
  const size = CLUSTER_SIZES[tier]
  return {
    html: `<div class="hfse-cluster hfse-cluster--t${tier + 1}" style="width:${size}px;height:${size}px;"><span>${count}</span></div>`,
    size,
  }
}

/* Stacked badge HTML — builds on the core .hfse-map-badge pill. */
export function stackedBadgeHtml(priceRange: string, count: number): string {
  return (
    `<div class="hfse-map-badge hfse-map-badge-stacked${priceRange ? '' : ' hfse-stack-count-only'}">` +
    (priceRange ? `<span class="hfse-stack-price">${priceRange}</span>` : '') +
    `<span class="hfse-stack-count">${count}</span>` +
    `</div>`
  )
}
